/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref, computed, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import { fetchDoctorProfile } from "@/services/doctorProfileService";
import { professionalBase } from "@/services/naaviApi/internalService";

export interface DoctorProfileOptions {
  translateFn?: (key: string) => string;
}

function normalizeProfileList(value: unknown): string[] {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item.trim() : item))
      .filter(
        (item) =>
          item !== null && item !== undefined && String(item).trim().length > 0,
      )
      .map((item) => String(item));
  }

  if (typeof value === "string") {
    return value
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  return [];
}

export function useDoctorProfile(options: DoctorProfileOptions = {}) {
  const route = useRoute();
  const { translateFn } = options;

  const doctor = ref<any>(null);
  const classes = ref<any[]>([]);
  const mapDoctors = ref<any[]>([]);
  const isLoading = ref(false);
  const loadError = ref<Error | null>(null);
  const isMapModalVisible = ref(false);

  const aboutMeList = computed(() =>
    normalizeProfileList(doctor.value?.user?.aboutMe),
  );
  const educationList = computed(() =>
    normalizeProfileList(doctor.value?.user?.education),
  );
  const servicesList = computed(() =>
    normalizeProfileList(doctor.value?.user?.services),
  );
  const showAboutSection = computed(() => aboutMeList.value.length > 0);
  const hasExperienceSection = computed(
    () => educationList.value.length > 0 || showAboutSection.value,
  );
  const hasServicesSection = computed(() => servicesList.value.length > 0);

  // True only when at least one map-renderable doctor has valid coordinates, OR the tenant
  // itself has lat/long set. Hides the empty map block when there's nothing to show.
  const hasMap = computed(() => {
    const validCoords = (lat: unknown, lng: unknown) =>
      typeof lat === "number" &&
      typeof lng === "number" &&
      !(lat === 0 && lng === 0);
    const candidates: any[] = mapDoctors.value.length
      ? mapDoctors.value
      : doctor.value?.user
      ? [doctor.value.user]
      : [];
    const anyDoctorHasCoords = candidates.some((d) =>
      validCoords(d?.position?.lat, d?.position?.lng),
    );
    if (anyDoctorHasCoords) return true;
    const tenantInfo = doctor.value?.tenantInfo;
    return validCoords(tenantInfo?.latitude, tenantInfo?.longitude);
  });

  async function loadProfile(onScheduleLoaded?: (schedule: any, doctorUser: any) => void): Promise<void> {
    const doctorId = Number(route.params.id);
    if (!doctorId) {
      doctor.value = null;
      return;
    }

    isLoading.value = true;
    loadError.value = null;

    const tenantIdParam = route.params.tenantId
      ? String(route.params.tenantId)
      : null;

    // Step 1: lightweight base profile from naavi cache (user + resource + tenant).
    // This guarantees the page renders even if availability has no slots today.
    let baseError: Error | null = null;
    let resourceId: number | null = null;
    if (tenantIdParam) {
      try {
        const base = await professionalBase(tenantIdParam, doctorId);
        doctor.value = {
          id: doctorId,
          tenantId: base.tenantId,
          user: base.user,
          resource: base.resource,
          tenantInfo: base.tenant,
        };
        const rid = (base.resource as { id?: unknown } | null)?.id;
        if (typeof rid === "number") resourceId = rid;
      } catch (error) {
        baseError = error as Error;
      }
    }

    // Step 2: enrich with availability + classes + schedule. Pass resourceId when known so
    // the server filters to a single doctor (much smaller payload than the tenant-wide search).
    let profileError: Error | null = null;
    try {
      const profile = await fetchDoctorProfile(
        { doctorId, tenantId: tenantIdParam, resourceId },
        { translate: translateFn },
      );

      if (profile.doctor) {
        // Merge availability data over the base — availability wins on overlapping fields.
        doctor.value = { ...(doctor.value ?? {}), ...profile.doctor };
        classes.value = profile.classes;
        mapDoctors.value = profile.mapDoctors;
        onScheduleLoaded?.(profile.schedule, profile.doctor?.user ?? null);
      } else if (doctor.value) {
        // Base loaded but no availability data: empty schedule, not a UI error.
        classes.value = [];
        mapDoctors.value = [];
        onScheduleLoaded?.(null, doctor.value?.user ?? null);
      }
    } catch (error) {
      profileError = error as Error;
    }

    // Real load error only when BOTH calls failed and we have nothing to show.
    if (!doctor.value) {
      loadError.value = baseError ?? profileError;
    } else {
      loadError.value = null;
    }
    isLoading.value = false;
  }

  function viewMapModal(): void {
    mapDoctors.value = doctor.value?.user ? [doctor.value.user] : [];
    isMapModalVisible.value = true;
  }

  function closeMapModal(): void {
    isMapModalVisible.value = false;
  }

  return {
    doctor,
    classes,
    mapDoctors,
    isLoading,
    loadError,
    isMapModalVisible,
    aboutMeList,
    educationList,
    servicesList,
    showAboutSection,
    hasExperienceSection,
    hasServicesSection,
    hasMap,
    loadProfile,
    viewMapModal,
    closeMapModal,
  };
}
